/** Version 2 remains the public wire format. No Fabric or browser dependency. */
export type MeasurementUnit = "px" | "mm" | "cm";
export type ObjectPayload = Record<string, unknown>;

/** Renderer payload stays opaque until the geometry adapter is migrated. */
export interface SerializedLayer extends Record<string, unknown> {
  data: ObjectPayload;
  type?: string;
  name?: string;
  index?: number;
}

export interface ScreenInput extends Record<string, unknown> {
  id?: string;
  name?: string;
  fileName?: string;
  width?: number | string;
  height?: number | string;
  measurementUnit?: unknown;
  pasteboardColor?: unknown;
  backgroundVisible?: boolean;
  layers?: SerializedLayer[];
}

export interface ScreenDocument extends ScreenInput {
  id: string;
  name: string;
  fileName: string;
  width: number | string;
  height: number | string;
  measurementUnit: MeasurementUnit;
  pasteboardColor: string;
  backgroundVisible: boolean;
  layers: SerializedLayer[];
}

export interface ProjectDocumentV2 extends Record<string, unknown> {
  projectVersion: 2;
  screens: ScreenInput[];
  currentScreenId?: string | null;
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

/** Validate before an import can replace the current document (SAVE-01). */
export function assertProjectDocument(
  value: unknown,
): asserts value is ProjectDocumentV2 {
  const invalid = () => {
    throw new Error("Неподдерживаемый формат проекта.");
  };
  if (
    !isRecord(value) ||
    value.projectVersion !== 2 ||
    !Array.isArray(value.screens) ||
    !value.screens.length
  )
    return invalid();

  for (const screen of value.screens) {
    if (!isRecord(screen)) return invalid();
    for (const key of ["id", "name", "fileName"]) {
      if (screen[key] != null && typeof screen[key] !== "string")
        return invalid();
    }
    // Missing layers are supported by older empty documents; malformed layers
    // must never be silently converted into an empty page.
    if (screen.layers != null) {
      if (!Array.isArray(screen.layers)) return invalid();
      for (const layer of screen.layers) {
        if (!isRecord(layer) || !isRecord(layer.data)) return invalid();
      }
    }
  }
}

export function projectExtensions(
  project: Record<string, unknown>,
): Record<string, unknown> {
  const extras = { ...project };
  delete extras.projectVersion;
  delete extras.screens;
  delete extras.currentScreenId;
  return extras;
}

export function buildProjectDocument(
  screens: ScreenInput[],
  currentScreenId: string | null,
  extensions: Record<string, unknown> = {},
): ProjectDocumentV2 {
  return { ...extensions, projectVersion: 2, screens, currentScreenId };
}
